import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { whyChooseData } from '../data/mock';
import { Zap, Smartphone, Code2, Sparkles } from 'lucide-react';

const iconMap = { Zap, Smartphone, Code2 };

const FeatureCard = ({ feature, index }) => {
  const IconComponent = iconMap[feature.icon];
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={cardRef}
      className="group relative p-8 rounded-3xl ios-card overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
      whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
    >
      {/* Hover glow */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-violet-500/15 via-transparent to-blue-500/15 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      />
      
      {/* Icon */}
      <motion.div 
        className="relative z-10 w-16 h-16 bg-gradient-to-br from-violet-500/30 to-blue-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500"
        whileHover={{ rotate: 5 }}
      >
        {IconComponent && <IconComponent size={32} className="text-violet-400" />}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/30 to-blue-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
      </motion.div>

      <h3 className="relative z-10 text-xl font-semibold text-white mb-3 group-hover:text-gradient-violet transition-all duration-300">
        {feature.title}
      </h3>
      <p className="relative z-10 text-white/50 leading-relaxed group-hover:text-white/70 transition-colors duration-300">
        {feature.description}
      </p>

      {/* Accent line */}
      <motion.div 
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-violet-500 via-purple-500 to-blue-500 rounded-full"
        initial={{ width: 0 }}
        whileHover={{ width: "100%" }}
        transition={{ duration: 0.4 }}
      />
    </motion.div>
  );
};

const WhyChooseSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], [0, -150]);

  return (
    <section ref={sectionRef} id="why-choose" className="py-32 lg:py-40 relative overflow-hidden">
      {/* Parallax Background */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: backgroundY }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-violet-600/15 via-purple-600/10 to-blue-600/15 blur-[200px]" />
      </motion.div>

      {/* Floating elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
          className="absolute top-32 left-[8%] w-20 h-20 border border-blue-500/20 rounded-full animate-float-slow"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -80]) }}
        />
        <motion.div 
          className="absolute bottom-32 right-[12%] w-14 h-14 bg-gradient-to-br from-violet-500/20 to-transparent rounded-lg rotate-45 animate-float"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, 80]) }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 glass px-5 py-2.5 mb-6 rounded-full"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Sparkles size={16} className="text-violet-400" />
            <span className="text-violet-400 text-sm font-semibold tracking-wider">THE ADVANTAGE</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Why Choose <span className="text-gradient-mixed">Me?</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            Building websites isn't just about code. It's about creating experiences that drive results.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {whyChooseData.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>

        {/* Stats */}
        <motion.div 
          className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 py-12 ios-card rounded-3xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {[
            { value: "24/7", label: "Support" },
            { value: "99.9%", label: "Uptime" },
            { value: "Free", label: "30-Day Support" }
          ].map((stat, index) => (
            <motion.div 
              key={index} 
              className={`text-center ${index === 1 ? 'border-y md:border-y-0 md:border-x border-white/10 py-8 md:py-0' : ''}`}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-gradient-mixed mb-2">{stat.value}</div>
              <p className="text-white/40 text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
